import { z } from 'zod';
import type { TFunction } from 'i18next';
import { MFA_IDENTITY_TYPES } from '@/entities/mfa-identity';
import {
  PASSWORD_LOWER_VALIDATION_REGEX,
  PASSWORD_UPPER_VALIDATION_REGEX,
  PASSWORD_VALIDATION_REGEX,
} from '@/shared/config';

const createLoginFormSchema = (t: TFunction) => {
  const required = t('authPages.validation.requiredField');
  const minLength = t('authPages.validation.passwordRules.minLength');
  const upperLower = t('authPages.validation.passwordRules.uppercaseLowercase');
  const digit = t('authPages.validation.passwordRules.digit');
  const allowedChars = t('authPages.validation.passwordRules.specialChars');

  const emailSchema = z
    .string()
    .nonempty({ message: required })
    .email({ message: t('authPages.validation.emailInvalid') });

  const passwordSchema = z
    .string()
    .nonempty({ message: required })
    .refine(val => PASSWORD_VALIDATION_REGEX.test(val), {
      message: allowedChars,
    })
    .min(10, { message: minLength })
    .refine(
      val =>
        PASSWORD_LOWER_VALIDATION_REGEX.test(val) &&
        PASSWORD_UPPER_VALIDATION_REGEX.test(val),
      {
        message: upperLower,
      }
    )
    .refine(val => /[0-9]/.test(val), {
      message: digit,
    });

  return z.object({
    login: emailSchema,
    password: passwordSchema,
  });
};

const createLoginMfaFormSchema = (t: TFunction) => {
  const required = t('authPages.validation.requiredField');
  const minLength = t('authPages.validation.passwordRules.minLength');
  const upperLower = t('authPages.validation.passwordRules.uppercaseLowercase');
  const digit = t('authPages.validation.passwordRules.digit');
  const allowedChars = t('authPages.validation.passwordRules.specialChars');
  const otpIncorrect = t('authPages.validation.otpIncorrect');

  const emailSchema = z
    .string()
    .nonempty({ message: required })
    .email({ message: t('authPages.validation.emailInvalid') });

  const passwordSchema = z
    .string()
    .nonempty({ message: required })
    .refine(
      val => /^[A-Za-z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]+$/.test(val),
      {
        message: allowedChars,
      }
    )
    .min(10, { message: minLength })
    .refine(val => /[a-z]/.test(val) && /[A-Z]/.test(val), {
      message: upperLower,
    })
    .refine(val => /[0-9]/.test(val), {
      message: digit,
    });

  const otpCodeSchema = z
    .string()
    .regex(/^\d+$/, { message: otpIncorrect })
    .nonempty({ message: required });

  return z.object({
    login: emailSchema,
    password: passwordSchema,
    twoFA: z.object({
      state: z.string(),
      code: otpCodeSchema,
      identityType: z.enum(MFA_IDENTITY_TYPES),
    }),
    source: z.string(),
  });
};

export { createLoginFormSchema, createLoginMfaFormSchema };
